import { useMutation } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Heading from 'shopper/components/Heading';
import RadioButton from 'shopper/components/RadioButton';
import Switch from 'shopper/components/Switch';
import Text from 'shopper/components/Text';

import StatusAlertWarn from 'components/StatusAlert/Warn';
import FormHeading from 'components/SuggestNewItem/FormHeading';

import useModal from 'hooks/useModal';

import { sendEvent } from 'lib/gtag';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setLocalStorage,
} from 'lib/localStorage';
import placeholder from 'lib/placeholder';

import { useUser } from 'providers/UserProvider';

import { postNewThread } from 'services/thread';

import { TOOLS } from 'constants/textEditor';

const TextEditor = dynamic(() => import('components/TextEditor'), {
  ssr: false,
  loading: () => null,
});

const SuccessModal = dynamic(() => import('./SuccessModal'), { ssr: false });

const MAX_LENGTH_TITLE_THREAD = 100;
const RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY = 'thread.form-recovery';
const RADIO_BUTTON_OPTIONS = [
  {
    label: 'Pergunta',
    value: 'QUESTION',
    description:
      'Quer tirar uma dúvida? Precisa de assistência com alguma compra ou loja? Selecione esta opção e pergunte à comunidade.',
  },
  {
    label: 'Discussão',
    value: 'DISCUSSION',
    description:
      'Gostaria de compartilhar sua opinião sobre um produto ou compra? Tem dicas ou conselhos para compartilhar? Selecione esta opção para iniciar uma discussão com a comunidade.',
  },
];

const isLineBreak = (text) => text === '<br/>';

const NewThreadForum = ({ gaEventCategory }) => {
  const { isLoggedIn, runCallbackIfLoggedIn } = useUser();
  const [isFormSubmitEnabled, setFormSubmitEnabled] = useState(false);
  const [receiveNotifications, setReceiveNotifications] = useState(true);
  const [recoveryFormData, setRecoveryFormData] = useState(null);
  const { showModal } = useModal();
  const radioRef = useRef([]);

  const {
    control,
    formState,
    reset: formReset,
    getValues,
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      threadTitle: '',
      threadText: '',
      threadTypeName: '',
      threadSubscription: false,
    },
    mode: 'onChange',
    reValidateMode: 'onBlur',
  });

  const { mutate: mutateNewThread, reset } = useMutation({
    mutationFn: postNewThread,
    onSuccess: ({ threadId }) => {
      sendEvent({
        category: gaEventCategory,
        action: 'submit_success',
      });

      reset();
      formReset();
      removeFromLocalStorage(RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY);

      showModal(SuccessModal, {
        threadId,
      });
    },
  });

  useEffect(() => {
    const data = getFromLocalStorage(RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY);

    if (data) {
      setRecoveryFormData(JSON.parse(data));
    }

    return () => {
      removeFromLocalStorage(RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY);
    };
  }, []);

  useEffect(() => {
    if (formState.isSubmitting || formState.isSubmitted) {
      setFormSubmitEnabled(false);
      return;
    }

    const formHasErrors = Object.values(formState.errors).length;
    const isRequiredFieldsFilled = Object.values(
      getValues(['threadTitle', 'threadText', 'threadTypeName'])
    ).every((value) => !!value && !isLineBreak(value));

    if (!formHasErrors && isRequiredFieldsFilled) {
      if (!isFormSubmitEnabled) {
        sendEvent({
          category: gaEventCategory,
          action: 'enabled_submit',
        });

        setFormSubmitEnabled(true);
      }
    } else {
      setFormSubmitEnabled(false);
    }
  }, [watch(['threadTitle', 'threadText', 'threadTypeName'])]);

  const onSubmit = (newThreadData) => {
    sendEvent({
      category: gaEventCategory,
      action: 'submit_attempt',
    });

    if (!isLoggedIn) {
      setLocalStorage(
        RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY,
        JSON.stringify(newThreadData)
      );
    }

    runCallbackIfLoggedIn(() => {
      mutateNewThread(newThreadData);
    });
  };

  useEffect(() => {
    if (recoveryFormData && isLoggedIn) {
      onSubmit(recoveryFormData);
    }
  }, [isLoggedIn]);

  const onSwitchClick = () => {
    sendEvent({
      category: gaEventCategory,
      action: !receiveNotifications ? 'resubscribe_alert' : 'unsubscribe_alert',
    });

    setReceiveNotifications((prev) => !prev);
  };

  const onThreadTypeTextClick = (type) => radioRef.current[type].click();

  return (
    <form className="px-4" onSubmit={handleSubmit(onSubmit)}>
      <FormHeading
        className="mb-6 mt-4 md:mt-6"
        description={placeholder('descriptions.threadTitle')}
        inputProps={{
          control,
          label: placeholder('inputs.labels.threadTitle'),
          maxLength: MAX_LENGTH_TITLE_THREAD,
          name: 'threadTitle',
          placeholder: placeholder('inputs.placeholders.threadTitle'),
          rules: {
            required: placeholder('inputs.errors.required.threadTitle'),
          },
          onFocus: () =>
            sendEvent({ category: gaEventCategory, action: 'focus_title' }),
        }}
        title={placeholder('titles.threadTitle')}
        inputLetterCounter
      />
      <Box className="px-4">
        <Heading as="h2" className="mb-3" size="size4">
          {placeholder('titles.threadText')}
        </Heading>
        <StatusAlertWarn>
          {placeholder('descriptions.threadTextDisclaimer')}
        </StatusAlertWarn>
        <Controller
          control={control}
          name="threadText"
          render={({ field: { onChange, ...fieldProps } }) => (
            <TextEditor
              className="mt-4"
              placeholder={placeholder('inputs.placeholders.threadText')}
              toolbarItems={[
                TOOLS.EMBEDDED_IMAGE,
                TOOLS.EMBEDDED_VIDEO,
                TOOLS.EMOJI,
              ]}
              onChange={(editorState) => {
                onChange(editorState.getContentInHtml());
              }}
              onFocus={() =>
                sendEvent({
                  category: gaEventCategory,
                  action: 'focus_content',
                })
              }
              {...fieldProps}
            />
          )}
          rules={{
            required: placeholder('inputs.errors.required.threadText'),
          }}
        />
        <Text className="mt-6">
          {placeholder('descriptions.threadTypeName')}
        </Text>
        <Controller
          control={control}
          name="threadTypeName"
          render={({ field }) =>
            RADIO_BUTTON_OPTIONS.map(({ description, label, value }) => (
              <Fragment key={label}>
                <RadioButton
                  {...field}
                  ref={(ref) => {
                    radioRef.current[value] = ref;
                  }}
                  className="mt-4"
                  id={label}
                  label={label}
                  name="threadTypeName"
                  value={value}
                />
                <Text
                  className="cursor-pointer text-neutral-high-500 dark:text-neutral-high-300"
                  size="size2"
                  onClick={() => onThreadTypeTextClick(value)}
                >
                  {description}
                </Text>
              </Fragment>
            ))
          }
        />
        <div className="mt-4 flex flex-col items-center justify-between border-t-2 border-solid border-neutral-high-200 py-4 dark:border-neutral-low-300 md:flex-row">
          <Controller
            control={control}
            defaultValue={false}
            name="threadSubscription"
            render={({ field }) => (
              <Switch
                checked={receiveNotifications}
                label={placeholder('switches.threadSubscription')}
                onClick={onSwitchClick}
                {...field}
              />
            )}
          />
          <Button
            className="mt-4 self-end md:mt-0 md:w-unset"
            disabled={!isFormSubmitEnabled}
            htmlType="submit"
            full
          >
            {placeholder('actions.sendThread')}
          </Button>
        </div>
      </Box>
    </form>
  );
};

NewThreadForum.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
};

export default NewThreadForum;
